import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useApiConfig } from "@visioni/a3s-client-api/app";
import { revokeUserAccessMutation } from "../mutations";
import { accessManagementQuery } from "../queries";

/**
 * Hook to revoke all of a user's access in the current tenant — every role
 * assignment and every direct camera permission — in a single server call.
 * Automatically invalidates the access management query on success so the
 * caller can navigate away immediately while the list refetches in the
 * background.
 *
 * Server-side the endpoint rejects self-revoke with a 403. Callers should
 * also guard client-side to surface a friendlier toast.
 *
 * @example
 * ```tsx
 * const { mutateAsync, isPending } = useRevokeUserAccess();
 *
 * const handleRevoke = async () => {
 *   await mutateAsync({ user: userId });
 *   router.back();
 * };
 * ```
 */
export function useRevokeUserAccess() {
  const apiConfig = useApiConfig();
  const queryClient = useQueryClient();

  const mutationApiConfig = {
    tenantId: apiConfig.tenant?.tenantId,
    apiBaseUrl: apiConfig.tenant?.apiBaseUrl,
    authenticatedFetch: apiConfig.authenticatedFetch,
  };

  return useMutation({
    ...revokeUserAccessMutation(mutationApiConfig, {}),
    onSuccess: () => {
      const queryKey = accessManagementQuery(apiConfig, {}).queryKey;
      void queryClient.invalidateQueries({ queryKey });
    },
  });
}
